import { useCallback, useState } from "react";
import debounce from "lodash/debounce";

/**
 * Encapsulates logic for debouncing an input.
 */
export const useDebouncedInput = <T,>({
  debounceTime = 300,
  initialValue,
  onDebounceChange,
}: {
  debounceTime?: number;
  initialValue: T;
  onDebounceChange?: (value: T) => void;
}) => {
  const [value, _setValue] = useState<T>(initialValue);
  const [debouncedValue, _setDebouncedValue] = useState<T>(initialValue);

  const dbValueSet = useCallback(
    (val: T) => {
      _setDebouncedValue(val);
      if (onDebounceChange) {
        onDebounceChange(val);
      }
    },
    [onDebounceChange]
  );

  // eslint-disable-next-line
  const setDebouncedValue = useCallback(
    debounce((term: T) => dbValueSet(term), debounceTime),
    []
  );

  const setValue = (val: T) => {
    _setValue(val);
    setDebouncedValue(val);
  };

  /** Resets both the value and debounced value instantly */
  const reset = () => {
    _setValue(initialValue);
    dbValueSet(initialValue);
  };

  return {
    value,
    debouncedValue,
    setValue,
    reset,
  };
};
